/**
 * CharterGroupBody — Phase-2/3 "Group Moderator" chat body.
 *
 * Self-contained: reads charter group WS state from Redux and owns its own
 * input/mention/ratification UI state. Does NOT manage the WS connection or a
 * Navbar — the host (CharterGroupPage or the in-game ChatRoom) owns those.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Bot, Loader2, Users } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectCharterSession,
  selectGroupMessages,
  selectGroupIsConnected,
  selectGroupIsStreaming,
  selectCurrentStage,
  selectRatificationVotes,
  selectGroupError,
  selectRatificationYesCount,
  selectTotalGroupMembers,
  selectPendingRenegotiationRequest,
  selectRatificationPromptActive,
  selectRatificationFinalizing,
  selectCharterStatus,
  selectMyPrepProfile,
  selectTeamProfiles,
  selectIsModeratorThinking,
  selectRatifiedCharter,
  selectGroupPagination,
  selectGroupIsLoadingMore,
} from '../../redux/selectors/charterSelectors'
import {
  sendGroupMessage,
  sendRatificationVote,
  loadMoreGroupMessages,
} from '../../redux/asyncThunks/charterWsThunks'
import { cancelRenegotiation } from '../../redux/asyncThunks/charterThunks'
import {
  appendGroupUserMessage,
  setGroupCharterStatus,
  setRenegotiationMode,
  setPendingRenegotiationRequest,
} from '../../redux/slices/charterGroupWsSlice'
import { updateSessionStatus } from '../../redux/slices/charterSlice'
import * as charterApi from '../../api/charter'
import { CharterChatMessages } from '../Shared/CharterChatMessages'
import { CharterChatInput } from '../Shared/CharterChatInput'
import { RatificationVotePanel } from '../Phase2/RatificationVotePanel'
import { RenegotiationVotePanel } from '../Renegotiation/RenegotiationVotePanel'
import { toGroupDisplayMessages } from '../../utils/charterDisplayMessages'
import { buildParticipantColors } from '../../utils/participantColors'

interface CharterGroupBodyProps {
  roomId: string
  /** When true the chat is reference-only (post-ratification): no input. */
  readOnly?: boolean
  /** Observer mode (professor monitoring): no input, no optimistic echo. */
  monitor?: boolean
}

export function CharterGroupBody({
  roomId,
  readOnly = false,
  monitor = false,
}: CharterGroupBodyProps) {
  const dispatch = useAppDispatch()

  const session = useAppSelector(selectCharterSession)
  const pendingRenegotiationRequest = useAppSelector(
    selectPendingRenegotiationRequest
  )
  const messages = useAppSelector(selectGroupMessages)
  const pagination = useAppSelector(selectGroupPagination)
  const isLoadingMore = useAppSelector(selectGroupIsLoadingMore)

  // Stable identity: an inline arrow here re-renders the whole message list on
  // every parent render, which defeats memo() during streaming.
  const handleLoadMore = useCallback(
    () => { dispatch(loadMoreGroupMessages()) },
    [dispatch]
  )
  const isConnected = useAppSelector(selectGroupIsConnected)
  const isStreaming = useAppSelector(selectGroupIsStreaming)
  const currentStage = useAppSelector(selectCurrentStage)
  const ratificationVotes = useAppSelector(selectRatificationVotes)
  const error = useAppSelector(selectGroupError)
  const yesCount = useAppSelector(selectRatificationYesCount)
  const totalMembers = useAppSelector(selectTotalGroupMembers)
  const ratificationPromptActive = useAppSelector(selectRatificationPromptActive)
  const ratificationFinalizing = useAppSelector(selectRatificationFinalizing)
  const charterStatus = useAppSelector(selectCharterStatus)
  const myProfile = useAppSelector(selectMyPrepProfile)
  const teamProfiles = useAppSelector(selectTeamProfiles)
  const isModeratorThinking = useAppSelector(selectIsModeratorThinking)
  const ratifiedCharter = useAppSelector(selectRatifiedCharter)

  const currentUser = useAppSelector((s) => s.user.user)
  const userName =
    currentUser?.name || currentUser?.email?.split('@')[0] || 'You'
  const currentUserId = String(currentUser?.id ?? '')
  // Ratification votes are keyed by RoomMember id (not the auth user id), so the
  // vote panel must identify the current member by their RoomMember id.
  const currentMemberId = String(myProfile?.member_id ?? '')

  const [inputValue, setInputValue] = useState('')
  const [isMentionActive, setIsMentionActive] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const inputDisabledByMode = monitor || readOnly

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || isStreaming || !isConnected || inputDisabledByMode) return
    const content = isMentionActive ? `@Group Moderator ${trimmed}` : trimmed
    dispatch(
      appendGroupUserMessage({
        content,
        sender_id: Number(currentUser?.id ?? 0),
        sender_display_name: userName,
        stage: currentStage ?? 'opening',
      })
    )
    dispatch(sendGroupMessage({ content, mentionsModerator: isMentionActive }))
    setInputValue('')
    setIsMentionActive(false)
  }, [
    dispatch,
    inputValue,
    isStreaming,
    isConnected,
    inputDisabledByMode,
    isMentionActive,
    currentUser,
    userName,
    currentStage,
  ])

  const handleRatificationVote = async (
    vote: boolean,
    concern?: string,
    sectionKey?: string
  ) => {
    await dispatch(
      sendRatificationVote({ vote, concern, section_key: sectionKey })
    )
  }

  const handleCancelAmendment = async () => {
    if (isCancelling) return
    setIsCancelling(true)
    try {
      await dispatch(
        cancelRenegotiation({ roomId, teamId: session?.team_id ?? 0 })
      )
      setConfirmCancelOpen(false)
    } finally {
      setIsCancelling(false)
    }
  }

  // Resilience fallback: while a re-negotiation is in progress, poll the
  // authoritative session status over HTTP. If it becomes 'locked' (e.g. a
  // teammate cancelled) revert immediately — this self-heals even when the live
  // `renegotiation_cancelled` WS event is missed (dropped/flaky socket). Bounded
  // to the renegotiation window; the interval stops once we're locked.
  useEffect(() => {
    if (monitor || charterStatus !== 'renegotiating') return
    const id = setInterval(async () => {
      try {
        const res = await charterApi.getCharterSession(roomId)
        if (res.data?.status === 'locked') {
          dispatch(
            setGroupCharterStatus({
              status: 'locked',
              version: res.data.version,
            })
          )
          dispatch(updateSessionStatus({ status: 'locked' }))
          dispatch(setRenegotiationMode(false))
          dispatch(setPendingRenegotiationRequest(null))
        }
      } catch {
        /* transient network error — try again next tick */
      }
    }, 4000)
    return () => clearInterval(id)
  }, [charterStatus, monitor, roomId, dispatch])

  // Real display names, keyed by RoomMember id to match how the vote panels
  // index votes. Built from the caller's own profile plus their teammates'
  // shared summaries — the same two sources the vote panels already rely on.
  //
  // Memoized so a keystroke (which only changes local input state) does not
  // recompute O(n) work or hand the memoized message list a new array/object
  // reference — that's what made typing lag with a large history.
  const memberNames = useMemo(
    () =>
      Object.fromEntries(
        [myProfile, ...teamProfiles]
          .filter((p): p is NonNullable<typeof p> => Boolean(p))
          .map((p) => [String(p.member_id), p.member_name])
      ) as Record<string, string>,
    [myProfile, teamProfiles]
  )

  const hasModeratorMessages = useMemo(
    () => messages.some((m) => m.role === 'moderator' && !m.is_streaming),
    [messages]
  )
  const isLocked =
    charterStatus === 'locked' || session?.status === 'locked'
  const sessionIsActive =
    ['in_progress', 'renegotiating'].includes(charterStatus ?? '') ||
    ['in_progress', 'renegotiating'].includes(session?.status ?? '')
  // A ratification vote is live for the CURRENT cycle when the backend said so
  // (event) or the latest moderator message is the ratification prompt. Scoping
  // to the last moderator message avoids a stale prior-cycle prompt latching the
  // UI (which previously froze the box on "Please vote to ratify" mid-amendment).
  const lastModerator = useMemo(
    () =>
      [...messages]
        .reverse()
        .find((m) => m.role === 'moderator' && !m.is_streaming),
    [messages]
  )
  const awaitingRatification =
    !isLocked &&
    !ratificationFinalizing &&
    (ratificationPromptActive ||
      lastModerator?.intervention_type === 'ratification_prompt' ||
      // An unplaced concern leaves the vote open.
      lastModerator?.intervention_type === 'ratification_concern_unplaced')

  // True from connect until the moderator's first (intro) message starts
  // streaming. Flips false the instant the opening streams in. Building the
  // briefing takes several seconds, so without this the student stares at an
  // empty chat and assumes it is broken.
  const noModeratorMessageYet = useMemo(
    () => !messages.some((m) => m.role === 'moderator'),
    [messages]
  )
  const moderatorPreparing =
    isConnected &&
    !ratifiedCharter &&
    noModeratorMessageYet &&
    (isModeratorThinking || sessionIsActive)

  const displayMessages = useMemo(
    () => toGroupDisplayMessages(messages, currentUserId),
    [messages, currentUserId]
  )

  // Memoised so the map keeps its identity between stream chunks; a new object
  // every render would re-render every bubble.
  const senderColors = useMemo(() => buildParticipantColors(messages), [messages])

  return (
    <div className='flex flex-col h-full min-h-0 py-9'>
      {/* Messages */}
      <div className='flex flex-col flex-1 min-h-0 ml-[2.5rem] px-14'>
        {/* Pending renegotiation vote banner */}
        {pendingRenegotiationRequest && currentUser && session && (
          <div className='mb-3'>
            <RenegotiationVotePanel
              request={pendingRenegotiationRequest}
              roomId={roomId}
              teamId={session.team_id}
              currentMemberId={currentMemberId}
              memberNames={memberNames}
            />
          </div>
        )}

        {!isConnected && messages.length === 0 ? (
          <div className='flex items-center justify-center flex-1 gap-2 text-gray-400'>
            <Loader2 className='h-5 w-5 animate-spin' />
            <span>Joining group session…</span>
          </div>
        ) : moderatorPreparing ? (
          <div className='flex items-center justify-center flex-1'>
            <div className='flex items-center gap-3 rounded-2xl border border-violet-100 bg-violet-50 px-5 py-4 shadow-sm'>
              <div className='flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center'>
                <Bot className='h-4 w-4 text-violet-600' />
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-800'>
                  Group Moderator
                </p>
                <div className='flex items-center gap-2 text-sm text-gray-500'>
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  <span>Analyzing member profiles…</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <CharterChatMessages
              messages={displayMessages}
              agentName='Group Moderator'
              hasMore={pagination?.has_more ?? false}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
              senderColors={senderColors}
            />
            {isModeratorThinking && (
              <div className='flex items-center gap-2 px-2 py-1 text-gray-400'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='text-sm'>Group Moderator is thinking…</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom row */}
      <div className='ml-[2.5rem] px-14 pt-2'>
        {/* Cancel amendment escape hatch — revert to the last locked version if
            an amendment gets stuck (e.g. a finalize that won't complete). */}
        {charterStatus === 'renegotiating' && !monitor && (
          <div className='mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
            <span className='text-xs text-amber-700'>
              Re-negotiation in progress. Stuck? You can revert to the last
              locked version.
            </span>
            <button
              onClick={() => setConfirmCancelOpen(true)}
              disabled={isCancelling}
              className='flex-shrink-0 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isCancelling ? 'Cancelling…' : 'Cancel amendment'}
            </button>
          </div>
        )}

        {/* Confirm dialog — in-app modal (replaces the native window.confirm). */}
        <Dialog
          open={confirmCancelOpen}
          onOpenChange={(o) => !o && !isCancelling && setConfirmCancelOpen(false)}
        >
          <DialogContent className='max-w-md'>
            <DialogHeader>
              <DialogTitle>Cancel amendment?</DialogTitle>
              <DialogDescription>
                Revert to the last locked version? The in-progress amendment will
                be discarded.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setConfirmCancelOpen(false)}
                disabled={isCancelling}
              >
                Keep editing
              </Button>
              <Button
                onClick={handleCancelAmendment}
                disabled={isCancelling}
                className='bg-amber-600 hover:bg-amber-700 text-white gap-1.5'
              >
                {isCancelling ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Cancelling…
                  </>
                ) : (
                  'Cancel amendment'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ratification in process — shown to every teammate once all votes are
            in, while the (initial OR amended) charter is being generated. */}
        {ratificationFinalizing && (
          <Alert className='mb-3 border-indigo-200 bg-indigo-50 text-indigo-900'>
            <Loader2 className='h-4 w-4 animate-spin' />
            <AlertDescription className='text-indigo-800'>
              ⏳ All votes are in — ratification in process. Finalizing your team
              charter…
            </AlertDescription>
          </Alert>
        )}

        {/* Ratification vote panel — shown whenever a ratification vote is live
            for the current cycle (initial or amendment); the in-progress banner
            takes over once finalization starts. */}
        {awaitingRatification && (
          <div className='mb-3'>
            <RatificationVotePanel
              totalMembers={totalMembers}
              yesCount={yesCount}
              votes={
                ratificationVotes as Record<
                  string,
                  { vote: boolean; voted_at: string }
                >
              }
              currentMemberId={currentMemberId}
              memberNames={memberNames}
              onVote={handleRatificationVote}
            />
          </div>
        )}

        {error && (
          <Alert variant='destructive' className='mb-3'>
            <AlertCircle className='h-4 w-4' />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!inputDisabledByMode && (
          <>
            {/* @Group Moderator mention chip. The moderator only replies to a
                tagged message, so untagged messages are student-to-student. */}
            <div className='flex items-center gap-2 mb-2'>
              <button
                title='Turn this on when you want the moderator to answer this message'
                aria-pressed={isMentionActive}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  isMentionActive
                    ? 'bg-indigo-600 border-indigo-600 text-white'
                    : 'bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100'
                }`}
                onClick={() => {
                  setIsMentionActive((prev) => !prev)
                  document
                    .querySelector<HTMLTextAreaElement>('textarea')
                    ?.focus()
                }}
              >
                <Users className='h-3.5 w-3.5' />
                @Group Moderator
              </button>
              <span className='text-[11px] text-slate-500'>
                {isMentionActive
                  ? 'The moderator will reply to this message.'
                  : 'The moderator stays out until you tag it — just type to talk with your teammates.'}
              </span>
            </div>

            <CharterChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              disabled={
                !isConnected ||
                awaitingRatification ||
                ratificationFinalizing ||
                isLocked ||
                (!hasModeratorMessages && sessionIsActive)
              }
              isStreaming={isStreaming}
              placeholder={
                awaitingRatification
                  ? 'Please vote to ratify the charter above'
                  : isLocked
                    ? 'This charter is locked (reference only)'
                    : isConnected
                      ? 'Share your thoughts… (Enter to send, Shift+Enter for new line)'
                      : 'Connecting…'
              }
            />
          </>
        )}
      </div>
    </div>
  )
}
